import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SearchStreet } from './../models/search-street.model';

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BuildingMarkerGroup } from 'src/app/modules/buildings-map/buildings-map.component';
import { BuildingFilterModel } from '../models/building-filter.model';
import { ExportBuildingsModel } from '../models/export-buildings.model';
import { ImportBuildingModel } from '../models/import-buildings.model';
import { environment } from './../../../environments/environment';
import { BuildingModel } from './../models/building.model';
import { ComplexBuildingModel } from './../models/complex-building.model';
import { BuildingManagementType } from './../models/enum.model';
import { ImportResultModel } from './../models/import-result.model';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class BuildingService {
  apiUrl = '/api/v1/building';

  constructor(
    private http: HttpClient,
    private translateService: TranslateService
  ) {
    if (environment.production || environment.test) {
      this.apiUrl = `${environment.bffUrl}${this.apiUrl}`;
    }
  }

  getAllMarkers(): Observable<BuildingMarkerGroup[]> {
    const language = this.translateService.currentLang;
    return this.http.get<BuildingMarkerGroup[]>(
      `${this.apiUrl}/markers?language=${language}`
    );
  }

  getAll(
    params: HttpParams,
    filter: BuildingFilterModel
  ): Observable<ComplexBuildingModel> {
    if (filter) {
      if (filter.municipalities) {
        filter.municipalities.forEach((x) => {
          params = params.append('municipalities', x);
        });
      }

      if (filter.periods) {
        filter.periods.forEach((x) => {
          params = params.append('periods', x);
        });
      }

      params = params.append('language', this.translateService.currentLang);

      if (filter.types) {
        filter.types.forEach((x) => {
          params = params.append('types', BuildingManagementType[x]);
        });
      }

      if (filter.street && filter.street.length > 0) {
        params = params.append('street', filter.street);
      }

      if (filter.streetNumber && filter.streetNumber.length > 0) {
        params = params.append('streetNumber', filter.streetNumber);
      }

      if (filter.searchLongitude > 0 && filter.searchLatitude > 0) {
        params = params.append(
          'searchLongitude',
          filter.searchLongitude.toString()
        );
        params = params.append(
          'searchLatitude',
          filter.searchLatitude.toString()
        );
      }
    }
    return this.http.get<ComplexBuildingModel>(`${this.apiUrl}`, {
      params: params,
    });
  }

  get(id: string): Observable<BuildingModel> {
    return this.http.get<BuildingModel>(`${this.apiUrl}/${id}`);
  }

  add(model: BuildingModel): Observable<BuildingModel> {
    return this.http.post<BuildingModel>(`${this.apiUrl}`, model, httpOptions);
  }

  update(model: BuildingModel): Observable<BuildingModel> {
    return this.http.put<BuildingModel>(
      `${this.apiUrl}/${model.id}`,
      model,
      httpOptions
    );
  }

  remove(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.apiUrl}/${id}`);
  }

  import(model: ImportBuildingModel): Observable<ImportResultModel> {
    return this.http.post<ImportResultModel>(
      `${this.apiUrl}/import?municipalityId=${model.municipalityId}&type=${model.importType}&language=${model.language}`,
      JSON.stringify(model.fileAsBase64),
      httpOptions
    );
  }

  export(model: ExportBuildingsModel): Observable<Blob> {
    return this.http.get<Blob>(
      `${this.apiUrl}/export?municipalityId=${model.municipalityId}&type=${model.exportType}&language=${model.language}`,
      { responseType: 'blob' as 'json' }
    );
  }

  clearAll(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/clear-all`);
  }

  searchByStreet(model: SearchStreet): Observable<string[]> {
    return this.http.post<string[]>(
      `${this.apiUrl}/search`,
      model,
      httpOptions
    );
  }
}
